import React, { useState, useEffect, useRef } from 'react';
import { Send, ShieldCheck, FileText, ArrowRight, Briefcase, MapPin, GraduationCap, Award, Users, UploadCloud, CheckCircle, X, Clock } from 'lucide-react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../Firebase';
import { mockCareers } from '../utils/seedData';

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState('');

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [exp, setExp] = useState('');
  const [qualification, setQualification] = useState('');
  const [coverNote, setCoverNote] = useState('');
  
  // Resume file state
  const [resumeData, setResumeData] = useState({ url: '', name: '', size: '' });
  const [fileError, setFileError] = useState('');

  const formRef = useRef(null);

  // Fetch jobs dynamically from Firestore
  useEffect(() => {
    const fetchJobs = async () => {
      setLoadingJobs(true);
      try {
        const snap = await getDocs(collection(db, 'careers'));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

        const merged = [...mockCareers];
        if (list.length > 0) {
          list.forEach((fbJ) => {
            const idx = merged.findIndex(j => j.id === fbJ.id);
            if (idx > -1) {
              merged[idx] = fbJ;
            } else {
              merged.push(fbJ);
            }
          });
        }
        setJobs(merged);
      } catch (error) {
        console.warn("Could not fetch jobs from Firestore, using default:", error);
        setJobs(mockCareers);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, []);

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    setFileError('');
    if (!file) return;

    // Check size limit (max 1.5MB for local base64 storage)
    if (file.size > 1.5 * 1024 * 1024) {
      setFileError('Resume file size must be under 1.5 MB.');
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setResumeData({
        url: reader.result,
        name: file.name,
        size: (file.size / 1024).toFixed(1) + ' KB'
      });
    };
    reader.readAsDataURL(file);
  };

  const removeResume = () => {
    setResumeData({ url: '', name: '', size: '' });
    const input = document.getElementById('resume-file-input');
    if (input) input.value = '';
  };

  const handleApplyClick = (jobTitle) => {
    setSelectedJob(jobTitle);
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const applicationId = `app_${Date.now()}`;
    const payload = {
      id: applicationId,
      applicationId,
      jobTitle: selectedJob,
      fullName,
      email,
      phone,
      experience: exp,
      qualification,
      coverNote,
      resumeUrl: resumeData.url || '',
      resumeName: resumeData.name || '',
      resumeSize: resumeData.size || '',
      status: 'Applied',
      createdAt: new Date().toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    };

    try {
      await setDoc(doc(db, 'career_applications', applicationId), payload);
    } catch (error) {
      console.warn("Could not save application in Firestore, saving locally:", error);
    }

    // Save locally for fallback
    try {
      const stored = localStorage.getItem('sahyadri_career_apps') || '[]';
      const apps = JSON.parse(stored);
      apps.unshift(payload);
      localStorage.setItem('sahyadri_career_apps', JSON.stringify(apps));
    } catch (e) { }

    setLoading(false);
    setFormSubmitted(true);

    // Reset Form
    setTimeout(() => {
      setFormSubmitted(false);
      setFullName('');
      setEmail('');
      setPhone('');
      setExp('');
      setQualification('');
      setCoverNote('');
      setSelectedJob('');
      removeResume();
    }, 4000);
  };

  const benefits = [
    { title: 'PF & Retirement Cover', desc: 'Secure retirement payouts with institutional Provident Fund contributions.' },
    { title: 'Comprehensive Health Insurance', desc: 'Medical coverage and hospital allowances for employees and dependents.' },
    { title: 'Performance Bonuses', desc: 'Attractive credit performance incentives aligned with cooperative targets.' },
    { title: 'Training & Development', desc: 'Periodic workshops on core banking systems, audit compliance, and cyber security.' }
  ];

  return (
    <div className="pt-[68px] bg-[#F7F9FC]">
      {/* Page Header */}
      <section
        className="relative overflow-hidden text-navy py-20 px-4 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/page header.png')" }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(0,0,0,0.01),transparent)] pointer-events-none" />
        <div className="max-w-4xl mx-auto space-y-4 relative z-10">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Work With Us</span>
          <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">Build a Rewarding Career in Cooperative Banking</h1>
          <p className="text-slate-600 text-sm sm:text-base font-normal max-w-2xl mx-auto">
            Join the Sahyadri Urban family. We offer progressive career paths, supportive work culture, and impactful contributions to Maharashtra's financial growth.
          </p>
        </div>
      </section>

      {/* Employee Culture & Benefits */}
      <section className="py-20 bg-white text-navy font-sans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Employee Benefits</span>
            <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">Why Join Sahyadri Urban?</h2>
            <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
              We provide an empowering ecosystem where banking professionals cultivate skills and serve communities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, i) => (
              <div key={i} className="bg-[#F8FAFC] border border-gray-200/80 rounded-3xl p-6 space-y-3 hover:bg-white hover:border-[#B31825] hover:shadow-xl transition-all duration-300 group">
                <div className="p-3 bg-primary/10 text-primary rounded-2xl w-fit group-hover:bg-[#B31825] group-hover:text-white transition-all">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <h3 className="font-montserrat font-bold text-base text-navy">{benefit.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed font-light">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Openings & Application Form */}
      <section className="py-20 bg-neutralBg text-navy border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">

          {/* Dynamic Job Listings */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 pb-4">
              <div>
                <span className="text-xs font-bold text-primary uppercase tracking-widest bg-primary/10 px-3 py-1 rounded-full w-fit block mb-1">Current Openings</span>
                <h2 className="font-montserrat font-extrabold text-2xl text-navy">Explore Available Positions</h2>
              </div>
              <span className="text-xs font-bold text-gray-500 bg-white px-3 py-1.5 rounded-xl border border-gray-200 shadow-sm w-fit">
                {jobs.length} Active Positions
              </span>
            </div>

            {loadingJobs ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-200">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#B31825] border-t-transparent mb-3" />
                <p className="text-xs font-semibold text-gray-500">Loading career vacancies...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="py-16 text-center text-gray-500 bg-white border border-gray-200 rounded-3xl p-8">
                <Briefcase className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <h4 className="font-montserrat font-bold text-sm text-navy">No Current Vacancies</h4>
                <p className="text-xs text-gray-400 mt-1">Please check back soon or submit your profile through the application form.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-7 hover:border-[#B31825] hover:shadow-xl transition-all duration-300 space-y-4 group relative"
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start flex-wrap gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-[#052046]/10 text-[#052046] px-2.5 py-0.5 rounded-md">
                            {job.department || 'General Banking'}
                          </span>
                          {job.vacancies && (
                            <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald/10 text-emerald px-2.5 py-0.5 rounded-md flex items-center">
                              <Users className="h-3 w-3 mr-1" /> {job.vacancies}
                            </span>
                          )}
                        </div>
                        <h3 className="font-montserrat font-extrabold text-lg text-navy group-hover:text-[#B31825] transition-colors pt-1">
                          {job.title}
                        </h3>
                        <div className="flex items-center space-x-3 text-xs text-gray-500 font-medium flex-wrap">
                          <span className="flex items-center"><MapPin className="h-3.5 w-3.5 text-[#B31825] mr-1 shrink-0" /> {job.location || 'Maharashtra'}</span>
                          {job.type && <span className="flex items-center"><Clock className="h-3.5 w-3.5 text-gray-400 mr-1 shrink-0" /> {job.type}</span>}
                        </div>
                      </div>
                      {job.salary && (
                        <div className="text-right">
                          <span className="text-xs text-golden-dark bg-golden/10 font-bold px-3 py-1.5 rounded-full inline-block border border-golden/20">
                            {job.salary}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {job.description && (
                      <p className="text-gray-600 text-xs sm:text-sm leading-relaxed font-light bg-neutralBg/60 p-3.5 rounded-2xl border border-gray-100">
                        {job.description}
                      </p>
                    )}

                    {/* Requirements & Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-600 pt-1">
                      {job.qualification && (
                        <div className="flex items-start space-x-2">
                          <GraduationCap className="h-4 w-4 text-[#B31825] shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-gray-700 block text-[11px]">Qualification:</span>
                            <span className="text-gray-500 font-light">{job.qualification}</span>
                          </div>
                        </div>
                      )}
                      {job.experience && (
                        <div className="flex items-start space-x-2">
                          <Award className="h-4 w-4 text-[#B31825] shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-gray-700 block text-[11px]">Experience:</span>
                            <span className="text-gray-500 font-light">{job.experience}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Skills Tags */}
                    {job.skills && (
                      <div className="space-y-1.5 pt-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Required Skills:</span>
                        <div className="flex flex-wrap gap-1.5">
                          {job.skills.split(',').map((skill, sIdx) => (
                            <span key={sIdx} className="text-[10px] bg-gray-100 text-gray-700 font-medium px-2.5 py-0.5 rounded-md border border-gray-200">
                              {skill.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bottom Action */}
                    <div className="text-xs text-gray-500 border-t border-gray-100 pt-3 flex justify-end items-center">
                      <button
                        onClick={() => handleApplyClick(job.title)}
                        className="inline-flex items-center space-x-1.5 px-4 py-2 bg-[#B31825] hover:bg-[#8e131d] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow-md shadow-[#B31825]/10"
                      >
                        <span>Apply For Position</span>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Interactive Application Form */}
          <div ref={formRef} className="lg:col-span-5 bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-xl sticky top-28 space-y-6">
            <div className="border-b border-gray-100 pb-4">
              <span className="text-[10px] font-bold text-[#B31825] uppercase tracking-wider block">Candidate Portal</span>
              <h3 className="font-montserrat font-extrabold text-xl text-navy flex items-center mt-0.5">
                <FileText className="h-5.5 w-5.5 mr-2.5 text-[#B31825]" />
                <span>Submit Job Application</span>
              </h3>
              <p className="text-xs text-gray-500 mt-1 font-light">Fill out your credentials below. Our HR division will contact shortlisted candidates.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {formSubmitted ? (
                <div className="py-12 text-center space-y-4 animate-fadeIn">
                  <div className="mx-auto h-16 w-16 rounded-full bg-emerald/10 flex items-center justify-center text-emerald text-3xl font-bold border border-emerald/20">
                    <CheckCircle className="h-8 w-8 text-emerald" />
                  </div>
                  <h4 className="text-lg font-bold text-navy font-montserrat">Application Submitted!</h4>
                  <p className="text-xs text-gray-500 leading-relaxed font-light max-w-sm mx-auto">
                    Your profile and application have been logged with Sahyadri HR. We will review your application and reach out via phone/email.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Applying for Position *</label>
                    <select
                      required
                      value={selectedJob}
                      onChange={(e) => setSelectedJob(e.target.value)}
                      className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-2.5 text-xs text-navy focus:outline-none focus:border-[#B31825] focus:ring-1 focus:ring-[#B31825] transition-all font-medium"
                    >
                      <option value="">-- Select Position --</option>
                      {jobs.map((j) => (
                        <option key={j.id} value={j.title}>{j.title} {j.location ? `(${j.location})` : ''}</option>
                      ))}
                      <option value="General Open Application">General Open Application (Other)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="e.g. Ramesh S. Shinde"
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs text-navy focus:outline-none focus:border-[#B31825] focus:ring-1 focus:ring-[#B31825] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mobile Number *</label>
                      <input
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        pattern="[0-9]{10}"
                        placeholder="10-digit number"
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs text-navy focus:outline-none focus:border-[#B31825] focus:ring-1 focus:ring-[#B31825] transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email ID *</label>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@email.com"
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs text-navy focus:outline-none focus:border-[#B31825] focus:ring-1 focus:ring-[#B31825] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Total Experience *</label>
                      <input
                        type="text"
                        required
                        value={exp}
                        onChange={(e) => setExp(e.target.value)}
                        placeholder="e.g. 3 Years in Banking"
                        className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs text-navy focus:outline-none focus:border-[#B31825] focus:ring-1 focus:ring-[#B31825] transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Highest Qualification *</label>
                    <input
                      type="text"
                      required
                      value={qualification}
                      onChange={(e) => setQualification(e.target.value)}
                      placeholder="e.g. B.Com / M.Com / MBA Finance"
                      className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs text-navy focus:outline-none focus:border-[#B31825] focus:ring-1 focus:ring-[#B31825] transition-all"
                    />
                  </div>

                  {/* Resume Upload Box */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Upload Resume (PDF / DOC) *</label>
                    {!resumeData.url ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-2xl p-4 text-center hover:border-[#B31825] transition-colors bg-[#F8FAFC]">
                        <input
                          id="resume-file-input"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleResumeChange}
                          className="hidden"
                        />
                        <label htmlFor="resume-file-input" className="cursor-pointer flex flex-col items-center justify-center space-y-1">
                          <UploadCloud className="h-6 w-6 text-[#B31825]" />
                          <span className="text-xs font-bold text-navy">Click to attach your CV / Resume</span>
                          <span className="text-[10px] text-gray-400">PDF, DOC, DOCX (Max 1.5MB)</span>
                        </label>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-emerald/10 border border-emerald/20 rounded-2xl">
                        <div className="flex items-center space-x-2.5 overflow-hidden">
                          <FileText className="h-5 w-5 text-emerald shrink-0" />
                          <div className="truncate">
                            <p className="text-xs font-bold text-navy truncate">{resumeData.name}</p>
                            <span className="text-[10px] text-gray-500">{resumeData.size}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeResume}
                          className="p-1 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                          title="Remove file"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    {fileError && <p className="text-[10px] text-red-500 mt-1 font-semibold">{fileError}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cover Note / Remarks</label>
                    <textarea
                      rows="3"
                      value={coverNote}
                      onChange={(e) => setCoverNote(e.target.value)}
                      placeholder="Briefly state your key skills, previous branch experience, and notice period..."
                      className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-3.5 py-2 text-xs text-navy focus:outline-none focus:border-[#B31825] focus:ring-1 focus:ring-[#B31825] transition-all"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 bg-gradient-to-r from-[#B31825] to-[#8e131d] text-white font-bold rounded-xl hover:opacity-95 transition-opacity flex items-center justify-center space-x-2 shadow-lg shadow-[#B31825]/20 text-xs uppercase tracking-wider"
                  >
                    <span>{loading ? 'Submitting Application...' : 'Submit Application'}</span>
                    <Send className="h-4 w-4" />
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
